import React, { useState, useEffect, useRef, useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { COTIZACION_STATES } from '../../assets/data/arrays'
import { useNavigate } from 'react-router-dom'
import OrderItemsTable from '../tables/OrderItemsTable'
import type {
  CotizacionData,
  CotizacionFormProps
} from '../../types/cotizacionFormTypes'
import {
  getMeta,
  esCiudadMetropolitana,
  renderDepartamentoOptions as renderDepartamentoOptionsUtil,
  renderCiudadOptions as renderCiudadOptionsUtil
} from '../../utils/cotizacionFormUtils'
import { createCustomerIfNeeded, ensureProductNames, prepareFormData as prepareFormDataService } from '../../services/cotizacionService'
import {
  applyClienteNetoToggleCotizacion,
  isClienteNetoActive
} from '../../utils/clienteNetoOrder'
import { 
  useCotizacionValidation,
  useCotizacionForm,
  useCotizacionProducts,
  useCotizacionCustomer
} from '../../hooks/cotizacion'
import { Input, Select, Button, Switch } from '../ui'
import { FormField } from '../ui/Form'
import SectionTitle from '../ui/SectionTitle'
import { CustomerInfoSection, AddressSection, ProductSearchInput, ShippingCostInput, NotesModal } from './shared'
import { InformationCircleIcon, XMarkIcon, CheckIcon, ArrowPathIcon, CubeIcon } from '@heroicons/react/24/outline'

// Re-exportar CotizacionData para mantener compatibilidad con imports existentes
export type { CotizacionData } from '../../types/cotizacionFormTypes'

const CotizacionForm: React.FC<CotizacionFormProps> = (props) => {
  const {
    initialData,
    mode,
    onSubmit,
    onFormChange,
    tiposDeDocumento: tiposDoc = [],
    departamentos: deptos = [],
    ciudades: cities = []
  } = props

  // Obtener el usuario de localStorage para inicializar el agente si es modo create
  const userLS = JSON.parse(localStorage.getItem('user') || '{}')

  // Hook de manejo del formulario
  const {
    formData,
    setFormData,
    handleInputChange,
    updateMetaData
  } = useCotizacionForm({
    initialData,
    mode,
    departamentos: deptos,
    ciudades: cities,
    onFormChange
  })
  const [costoEnvioInputValue, setCostoEnvioInputValue] = useState('0')
  const [showNoteModal, setShowNoteModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()
  
  // Refs para evitar loops infinitos en la sincronización
  const isSyncingRef = useRef(false)
  const lastCostoEnvioRef = useRef<string>('')
  const costoEnvioInitializedRef = useRef(false)
  const lastInitialDataIdRef = useRef<string | number | null>(null)

  // Hook de validación
  const {
    errors,
    setErrors,
    validateForm,
    isStockAvailable
  } = useCotizacionValidation({ formData, mode })

  // Hook de productos
  const {
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    productDetails,
    expandedCombos,
    loadingDetails,
    handleSearchChange,
    handleSelectProduct,
    handleQuantityChange,
    handleDiscountChange,
    handlePriceChange,
    handleRemoveProduct,
    toggleCombo,
    setShowResults,
    calculateTotalsWithProductDetails,
    markLineItemsCnApplied,
    clearCnAppliedSkus
  } = useCotizacionProducts({
    formData,
    setFormData,
    mode,
    costoEnvioInputValue
  })

  const handleClienteNetoChange = useCallback((checked: boolean) => {
    let linesToMark: typeof formData.line_items = []
    setFormData((prev) => {
      const result = applyClienteNetoToggleCotizacion(
        prev,
        checked,
        productDetails,
        costoEnvioInputValue
      )
      if (!result.changed) {
        return prev
      }
      if (checked) {
        linesToMark = result.line_items
      }
      return {
        ...prev,
        line_items: result.line_items,
        extraDetails: result.extraDetails,
        total: result.total
      }
    })
    if (checked && linesToMark?.length) {
      markLineItemsCnApplied(linesToMark)
    } else if (!checked) {
      clearCnAppliedSkus()
    }
  }, [setFormData, productDetails, costoEnvioInputValue, markLineItemsCnApplied, clearCnAppliedSkus])

  // Hook de cliente
  const {
    clienteEditable,
    validandoCliente,
    handleValidarCliente
  } = useCotizacionCustomer({
    formData,
    setFormData,
    setErrors,
    mode
  })


  // Efecto para actualizar el destino de la cotización cuando cambia la ciudad de envío
  useEffect(() => {
    if (formData.shippingAddress?.city) {
      const esMetropolitana = esCiudadMetropolitana(formData.shippingAddress.city)
      const nuevoDestino = esMetropolitana ? 'metropolitana' : 'nacional'
      if (formData.extraDetails?.destino_cotizacion !== nuevoDestino) {
        handleInputChange('extraDetails', 'destino_cotizacion', nuevoDestino)
      }
    }
  }, [formData.shippingAddress?.city, formData.extraDetails?.destino_cotizacion, handleInputChange])

  useEffect(() => {
    // Establecer el role_id al cargar el formulario
    if (userLS.id && !formData.agente) {
      handleInputChange('agente', '', userLS.id)
    }
  }, [userLS.id, formData.agente, handleInputChange])

  // Inicializar el valor del costo de envío solo una vez cuando se carga initialData
  useEffect(() => {
    // Usar el ID de la cotización para detectar cambios reales en initialData
    // Si no hay ID, usar un hash simple basado en la existencia de initialData y su fecha de creación
    const currentInitialDataId = initialData?.cotizaciones_id || 
                                 initialData?.id || 
                                 (initialData?.date_created ? `created_${initialData.date_created}` : null) ||
                                 (initialData ? 'has_data' : null)
    
    // Solo inicializar cuando cambia el ID de initialData (nueva cotización cargada)
    // o cuando es la primera vez (modo create)
    const shouldInitialize = currentInitialDataId !== lastInitialDataIdRef.current
    
    if (!shouldInitialize) {
      return
    }
    
    // Actualizar la referencia del ID ANTES de procesar
    lastInitialDataIdRef.current = currentInitialDataId
    costoEnvioInitializedRef.current = false // Resetear para permitir reinicialización
    
    if (!initialData) {
      // En modo create sin initialData, inicializar con '0' solo una vez
      if (mode === 'create') {
        setCostoEnvioInputValue('0')
        costoEnvioInitializedRef.current = true
      }
      return
    }
    
    // Obtener el valor del costo de envío desde múltiples fuentes
    // Priorizar: envio > extraDetails.costo_envio > meta_data > '0'
    const costoEnvioValue = initialData.envio || 
                           initialData.extraDetails?.costo_envio || 
                           getMeta(initialData.meta_data || [], 'costo_envio') || 
                           '0'
    
    // Convertir a string si es número
    const costoEnvioStr = typeof costoEnvioValue === 'number' ? costoEnvioValue.toString() : (costoEnvioValue || '0')
    
    // Siempre actualizar cuando cambia initialData (modo edit/view)
    // Esto es crítico para que se muestre el valor correcto cuando se carga una cotización
    setCostoEnvioInputValue(costoEnvioStr)
    
    // Sincronizar con extraDetails y meta_data inmediatamente
    // No usar setTimeout aquí porque necesitamos que se sincronice correctamente
    handleInputChange('extraDetails', 'costo_envio', costoEnvioStr)
    updateMetaData('costo_envio', costoEnvioStr)
    
    // Marcar como inicializado
    costoEnvioInitializedRef.current = true
  }, [initialData, mode, handleInputChange, updateMetaData])

  useEffect(() => {
   // console.log('Datos de la cotización recibidos:', JSON.stringify(initialData, null, 2));
  }, [initialData])

  // Efecto para sincronizar forma de pago con método de pago
  useEffect(() => {
    if (formData.extraDetails?.payment_method) {
      let formaPagoValue = '6' // Por defecto contado
      if (formData.extraDetails.payment_method === 'cod' || formData.extraDetails.payment_method === 'cmdm' || formData.extraDetails.payment_method === 'addi') {
        formaPagoValue = '4' // Crédito (para cod, cmdm o addi)
      } else if (formData.extraDetails.payment_method === 'bold') {
        formaPagoValue = '6' // Contado (para bold)
      }
      handleInputChange('extraDetails', 'forma_pago', formaPagoValue)
    }
  }, [formData.extraDetails?.payment_method, handleInputChange])

  // Refs para almacenar las funciones y valores actuales
  const handleInputChangeRef = useRef(handleInputChange)
  const updateMetaDataRef = useRef(updateMetaData)
  const calculateTotalsRef = useRef(calculateTotalsWithProductDetails)
  const formDataRef = useRef(formData)
  const hasInitializedRef = useRef(false)
  
  // Actualizar refs cuando cambien
  useEffect(() => {
    handleInputChangeRef.current = handleInputChange
    updateMetaDataRef.current = updateMetaData
    calculateTotalsRef.current = calculateTotalsWithProductDetails
    formDataRef.current = formData
  }, [handleInputChange, updateMetaData, calculateTotalsWithProductDetails, formData])

  // Efecto para sincronizar automáticamente el costo de envío
  // SOLO se ejecuta cuando costoEnvioInputValue cambia (no cuando formData cambia)
  useEffect(() => {
    // Evitar loops infinitos
    if (isSyncingRef.current) {
      return
    }
    
    // Solo sincronizar si el valor realmente cambió
    if (lastCostoEnvioRef.current === costoEnvioInputValue) {
      return
    }
    
    const currentFormData = formDataRef.current
    const costoEnvioActual = currentFormData.extraDetails?.costo_envio?.toString() || '0'
    const metaCostoEnvio = getMeta(currentFormData.meta_data || [], 'costo_envio') || '0'
    
    // Si ya está sincronizado, no hacer nada
    if (costoEnvioActual === costoEnvioInputValue && metaCostoEnvio === costoEnvioInputValue) {
      lastCostoEnvioRef.current = costoEnvioInputValue
      return
    }
    
    isSyncingRef.current = true
    lastCostoEnvioRef.current = costoEnvioInputValue
    
    // Usar setTimeout para ejecutar después del render y evitar loops
    setTimeout(() => {
      try {
        const latestFormData = formDataRef.current
        
        // Sincronizar con extraDetails solo si es diferente
        const latestCostoEnvio = latestFormData.extraDetails?.costo_envio?.toString() || '0'
        if (latestCostoEnvio !== costoEnvioInputValue) {
          handleInputChangeRef.current('extraDetails', 'costo_envio', costoEnvioInputValue)
        }
        
        // Sincronizar con meta_data solo si es diferente
        const latestMetaCostoEnvio = getMeta(latestFormData.meta_data || [], 'costo_envio') || '0'
        if (latestMetaCostoEnvio !== costoEnvioInputValue) {
          updateMetaDataRef.current('costo_envio', costoEnvioInputValue)
        }
        
        // Actualizar el total cuando cambie el costo de envío
        if (latestFormData.line_items && latestFormData.line_items.length > 0) {
          const totals = calculateTotalsRef.current(latestFormData.line_items, costoEnvioInputValue)
          const currentTotal = latestFormData.total?.toString() || '0'
          if (currentTotal !== totals.total) {
            setFormData(prev => ({
              ...prev,
              total: totals.total
            }))
          }
        }
      } finally {
        isSyncingRef.current = false
      }
    }, 0)
  }, [costoEnvioInputValue, setFormData])

  // 1. Al inicializar el formulario, aseguro que el costo de envío esté en meta_data si existe en initialData o en extraDetails
  useEffect(() => {
    // Solo ejecutar una vez durante la inicialización
    if (hasInitializedRef.current) {
      return
    }
    
    if (!initialData) {
      if (mode === 'create') {
        setFormData(prev => {
          const meta_data = Array.isArray(prev.meta_data) ? [...prev.meta_data] : []
          const idx = meta_data.findIndex((m: any) => m.key === 'costo_envio')
          if (idx === -1) {
            meta_data.push({ key: 'costo_envio', value: '0' })
          }
          return {
            ...(prev as CotizacionData),
            status: COTIZACION_STATES[0]?.value || '',
            meta_data
          }
        })
      }
      hasInitializedRef.current = true
      return
    }
    setFormData(prev => {
      // Migrar datos completos de billing y shipping a billingAddress y shippingAddress
      const billingAddress = { ...initialData.billing, ...prev.billingAddress }
      const shippingAddress = { ...initialData.shipping, ...prev.shippingAddress }
      // Buscar tipo_documento y documento en meta_data (pueden venir como 'tipo_documento' y 'documento')
      let tipo_documento = (billingAddress && billingAddress.tipo_documento) || ''
      let documentoValue = (billingAddress && billingAddress.documento) || ''
      let meta_data = prev.meta_data || initialData.meta_data || []
      if (Array.isArray(initialData.meta_data)) {
        const tipoDocMeta = initialData.meta_data.find(m => m.key === 'tipo_documento' || m.key === 'tipo_documento')
        if (tipoDocMeta) tipo_documento = tipoDocMeta.value
        const docMeta = initialData.meta_data.find(m => m.key === 'documento' || m.key === 'documento')
        if (docMeta) documentoValue = docMeta.value
      }
      if (tipo_documento && !meta_data.find((m: any) => m.key === 'tipo_documento')) {
        meta_data = [...meta_data, { key: 'tipo_documento', value: tipo_documento }]
      }
      if (documentoValue && !meta_data.find((m: any) => m.key === 'documento')) {
        meta_data = [...meta_data, { key: 'documento', value: documentoValue }]
      }
      // Añado o actualizo costo_envio en meta_data SOLO si no existe ya un valor válido
      const costoEnvioValue = initialData.envio || initialData.extraDetails?.costo_envio || '0'
      const idx = meta_data.findIndex((m: any) => m.key === 'costo_envio')
      if (idx >= 0) {
        if (!meta_data[idx].value || meta_data[idx].value === '0') {
          meta_data[idx] = { ...meta_data[idx], value: costoEnvioValue }
        }
      } else {
        meta_data.push({ key: 'costo_envio', value: costoEnvioValue })
      }
      // Aseguro que cada item de line_items tenga name y SKU correcto
      const line_items = (initialData.line_items || []).map(item => {
        // Para productos combo, usar el SKU del primer componente como referencia
        let displaySku = item.sku
        
        // Si es un combo y tiene componentes, usar el SKU del primer componente
        if (item.is_combo === 1 && item.components && item.components.length > 0) {
          displaySku = item.components[0].sku
        }
        
        return {
        ...item,
          name: item.name || item.product_name || '',
          // Usar el SKU de display para mostrar en la tabla
          display_sku: displaySku || item.sku || item.product_id
        }
      })
      return {
        ...prev,
        billingAddress,
        shippingAddress,
        line_items,
        meta_data,
        customer: {
          ...prev.customer,
          tipo_documento,
          documento: documentoValue
        },
        // --- Sincronizo forma_pago con payment_method si está en modo edit ---
        extraDetails: {
          ...prev.extraDetails,
          ...initialData.extraDetails,
          forma_pago: (initialData.extraDetails?.payment_method === 'cod' || initialData.extraDetails?.payment_method === 'cmdm' || initialData.extraDetails?.payment_method === 'addi') ? '4' : (initialData.extraDetails?.payment_method ? '6' : (initialData.extraDetails?.forma_pago || prev.extraDetails?.forma_pago))
        }
      }
    })
    
    // Marcar como inicializado después de actualizar formData
    hasInitializedRef.current = true
    
    // Si está en modo edición o vista, consultar automáticamente la información del cliente
    if (mode === 'edit' || mode === 'view') {
      let tipo_documento = ''
      let documentoValue = ''
      if (Array.isArray(initialData.meta_data)) {
        const tipoDocMeta = initialData.meta_data.find(m => m.key === 'tipo_documento' || m.key === 'tipo_documento')
        if (tipoDocMeta) tipo_documento = tipoDocMeta.value
        const docMeta = initialData.meta_data.find(m => m.key === 'documento' || m.key === 'documento')
        if (docMeta) documentoValue = docMeta.value
      }
      if (tipo_documento && documentoValue) {
        // Usar la función del hook para validar cliente automáticamente
        handleValidarCliente()
      }
    }
  }, [initialData, handleValidarCliente, mode, setFormData])




  // Submit optimizado para manejar create y update
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      toast.error('Hay errores en el formulario. Por favor, revisa los campos en rojo.', { position: 'top-center' })
      console.error('Errores de validación en CotizacionForm:', errors)
      console.log('Datos actuales del formulario:', formData)
      return
    }

    if (!onSubmit) return

    setIsSubmitting(true)
    try {
      // Preparar datos base usando el servicio
      const preparedData = prepareFormDataService(
        formData,
        costoEnvioInputValue,
        mode,
        calculateTotalsWithProductDetails
      )
      
      // Crear cliente solo en modo create
      if (mode === 'create') {
        await createCustomerIfNeeded(formData)
      }

      // Asegurar que los productos tengan nombres
      await ensureProductNames(formData.line_items || [])

      console.log('JSON enviado a la API para cotización:', JSON.stringify(preparedData, null, 2))
      onSubmit(preparedData)
      
    } catch (error: any) {
      console.error('Error preparando datos:', error)
      toast.error(error.message || 'Error al preparar los datos', { position: 'top-center' })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Array de días de vigencia para la cotización
  const expiration_limit = [
    { value: 5, label: '5 días' },
    { value: 10, label: '10 días' },
    { value: 15, label: '15 días' },
    { value: 30, label: '30 días' },
  ]

  // Función para convertir en pedido (sin status de cotización)
  const handleConvertirEnPedido = () => {
    const { status: _statusCotizacion, ...formSinEstado } = formData
    navigate('/orders/new', {
      state: {
        fromCotizacion: {
          ...formSinEstado,
          productDetails
        }
      }
    })
  }

  // Handlers para CustomerInfoSection
  const handleCustomerFirstNameChange = (value: string) => {
    handleInputChange('billingAddress', 'first_name', value)
  }

  const handleCustomerLastNameChange = (value: string) => {
    handleInputChange('billingAddress', 'last_name', value)
  }

  const handleCustomerEmailChange = (value: string) => {
    handleInputChange('customer', 'email', value)
  }

  const handleCustomerPhoneChange = (value: string) => {
    handleInputChange('billingAddress', 'phone', value)
  }

  const handleCustomerTipoDocumentoChange = (value: string) => {
    handleInputChange('customer', 'tipo_documento', value)
    updateMetaData('tipo_documento', value)
  }

  const handleCustomerDocumentoChange = (value: string) => {
    handleInputChange('customer', 'documento', value)
    updateMetaData('documento', value)
  }

  // Handlers para AddressSection
  const handleBillingAddressChange = (field: string, value: string) => {
    handleInputChange('billingAddress', field, value)
  }

  const handleShippingAddressChange = (field: string, value: string) => {
    handleInputChange('shippingAddress', field, value)
  }

  // Obtener valor de nota para NotesModal
  const getNoteValue = () => {
    if (mode === 'edit' || mode === 'view') {
      return getMeta(formData.meta_data || [], 'customer_note') || formData.extraDetails?.customer_note || ''
    }
    return formData.extraDetails?.customer_note || ''
  }

  const handleNoteChange = (value: string) => {
    handleInputChange('extraDetails', 'customer_note', value)
    updateMetaData('customer_note', value)
  }

  // Obtener valor de documento según modo
  const getDocumentoValue = () => {
    return mode === 'edit' ? (formData.customer?.documento || '') : (getMeta(formData.meta_data || [], 'documento') || '')
  }

  // Obtener valor de tipo documento según modo
  const getTipoDocumentoValue = () => {
    return mode === 'edit' ? (formData.customer?.tipo_documento || '') : (getMeta(formData.meta_data || [], 'tipo_documento') || '')
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50">
      {/* Botón flotante de notas */}
      <NotesModal
        showModal={showNoteModal}
        setShowModal={setShowNoteModal}
        note={getNoteValue()}
        onNoteChange={handleNoteChange}
        mode={mode}
        title="Notas de la Cotización"
        placeholder="Ingrese las notas de la cotización..."
        position="right-8"
        buttonStyle="rounded-full"
      />

      {/* Información General */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <SectionTitle icon={InformationCircleIcon}>Información General</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Estado */}
          <FormField
            label="Estado"
            required
            error={errors.status}
          >
            <Select
              value={formData.status || (mode === 'create' ? COTIZACION_STATES[0]?.value || '' : '')}
              onChange={(e) => handleInputChange('status', '', e.target.value)}
              disabled={true}
              options={[
                { value: '', label: 'Seleccione' },
                ...COTIZACION_STATES.map((estado) => ({
                  value: estado.value,
                  label: estado.label
                }))
              ]}
              placeholder="Seleccione"
              error={!!errors.status}
              errorMessage={errors.status}
              mode={mode}
            />
          </FormField>

          {/* Vigencia */}
          <FormField
            label="Vigencia (días)"
            required
            error={errors.expiration_limit}
          >
            <Select
              value={getMeta(formData.meta_data || [], 'expiration_limit') || ''}
              onChange={(e) => {
                handleInputChange('extraDetails', 'expiration_limit', e.target.value)
                updateMetaData('expiration_limit', e.target.value)
              }}
              disabled={mode === 'view'}
              options={[
                { value: '', label: 'Seleccione' },
                ...expiration_limit.map(opt => ({
                  value: opt.value.toString(),
                  label: opt.label
                }))
              ]}
              placeholder="Seleccione"
              error={!!errors.expiration_limit}
              errorMessage={errors.expiration_limit}
              mode={mode}
            />
          </FormField>

          {/* Agente */}
          <FormField
            label="Agente"
            required
            error={errors.agente}
          >
            <Input
              type="text"
              value={(() => {
                if (mode === 'create') {
                  return `${userLS.first_name || ''} ${userLS.last_name || ''}`.trim()
                }
                const agente = formData.agente as { first_name?: string; last_name?: string }
                if (formData.agente && typeof formData.agente === 'object' && 'first_name' in agente && 'last_name' in agente) {
                  return `${agente.first_name || ''} ${agente.last_name || ''}`.trim()
                }
                if (formData.agente) {
                  return formData.agente.toString()
                }
                return ''
              })()}
              readOnly
              error={!!errors.agente}
              errorMessage={errors.agente}
              mode={mode}
            />
            <input 
              type="hidden" 
              value={userLS.id || ''} 
              onChange={(e) => handleInputChange('agente', '', e.target.value)}
            />
          </FormField>

          {/* Fecha de Expiración */}
          <FormField label="Fecha de Expiración">
            {(() => {
              const fechaCreacion = formData.date_created ? new Date(formData.date_created) : null
              let diasVigencia = 0
              const metaVigencia = getMeta(formData.meta_data || [], 'expiration_limit')
              if (metaVigencia) {
                diasVigencia = parseInt(metaVigencia)
              } else if (formData.extraDetails?.expiration_limit) {
                diasVigencia = parseInt(formData.extraDetails.expiration_limit as any)
              }
              let fechaExpiracion: Date | null = null
              let expiracionStr = ''
              let vencido = false
              if (fechaCreacion && !isNaN(diasVigencia) && diasVigencia > 0) {
                fechaExpiracion = new Date(fechaCreacion)
                fechaExpiracion.setDate(fechaExpiracion.getDate() + diasVigencia)
                expiracionStr = fechaExpiracion.toLocaleDateString('es-CO', { year: 'numeric', month: '2-digit', day: '2-digit' })
                const hoy = new Date()
                hoy.setHours(0,0,0,0)
                if (fechaExpiracion <= hoy) {
                  vencido = true
                }
              }
              return (
                <>
                  <Input
                    type="text"
                    value={expiracionStr || ''}
                    readOnly
                    className={fechaExpiracion ? (vencido ? 'border-red-500' : 'border-green-500') : ''}
                    mode={mode}
                  />
                  {vencido && <span className="text-red-500 text-sm mt-1">plazo vencido</span>}
                </>
              )
            })()}
          </FormField>
        </div>
      </div>

      {/* Información del Cliente */}
      <CustomerInfoSection
        firstName={formData.billingAddress?.first_name || ''}
        lastName={formData.billingAddress?.last_name || ''}
        email={formData.customer?.email || ''}
        phone={formData.billingAddress?.phone || formData?.phone || ''}
        tipoDocumento={getTipoDocumentoValue()}
        documento={getDocumentoValue()}
        onFirstNameChange={handleCustomerFirstNameChange}
        onLastNameChange={handleCustomerLastNameChange}
        onEmailChange={handleCustomerEmailChange}
        onPhoneChange={handleCustomerPhoneChange}
        onTipoDocumentoChange={handleCustomerTipoDocumentoChange}
        onDocumentoChange={handleCustomerDocumentoChange}
        mode={mode}
        tiposDeDocumento={tiposDoc}
        errors={errors}
        clienteEditable={clienteEditable}
        onValidateCliente={handleValidarCliente}
        validandoCliente={validandoCliente}
        showValidateButton={mode !== 'edit'}
        firstNameSource="billingAddress"
        documentoField="documento"
      />

      {/* Direcciones */}
      <AddressSection
        billingAddress={formData.billingAddress || {}}
        shippingAddress={formData.shippingAddress || {}}
        onBillingAddressChange={handleBillingAddressChange}
        onShippingAddressChange={handleShippingAddressChange}
        mode={mode}
        departamentos={deptos}
        ciudades={cities}
        errors={errors}
        renderDepartamentoOptions={(addressType) => renderDepartamentoOptionsUtil(addressType, formData, deptos)}
        renderCiudadOptions={(addressType) => renderCiudadOptionsUtil(addressType, formData, cities)}
        showAddress2={false}
      />

      {/* Costo de Envío */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <ShippingCostInput
            value={costoEnvioInputValue}
            onChange={(value) => {
              setCostoEnvioInputValue(value)
            }}
            mode={mode}
            errors={errors}
            errorKey="costo_envio"
            label="Costo de Envío"
          />
        </div>
      </div>
        
      {/* Productos de la Cotización */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 flex-1">
            <SectionTitle icon={CubeIcon} noMargin>Productos de la Cotización</SectionTitle>
            <p className="text-sm text-gray-600 hidden sm:block">Nota: Las cotizaciones pueden crearse sin verificar stock disponible</p>
          </div>
          {mode !== 'view' && (
            <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2 w-full lg:w-auto">
              <Switch
                checked={isClienteNetoActive(formData.extraDetails)}
                onChange={handleClienteNetoChange}
                label="Cliente Neto"
              />
              <ProductSearchInput
                searchTerm={searchTerm || ''}
                searchResults={searchResults}
                isSearching={isSearching}
                showResults={showResults}
                onSearchChange={handleSearchChange}
                onSelectProduct={handleSelectProduct}
                onFocus={() => setShowResults(true)}
                onBlur={() => setTimeout(() => setShowResults(false), 200)}
                placeholder="Buscar producto por nombre o SKU"
              />
            </div>
          )}
          <p className="text-sm text-gray-600 sm:hidden">Nota: Las cotizaciones pueden crearse sin verificar stock disponible</p>
        </div>

        {/* Contenedor con scroll horizontal solo para la tabla */}
        <div className="w-full overflow-x-auto -mx-4 sm:mx-0">
          <div className="min-w-full px-4 sm:px-0">
            <OrderItemsTable
          lineItems={formData.line_items || []}
          productDetails={productDetails}
          loadingDetails={loadingDetails}
          expandedCombos={expandedCombos}
          mode={mode}
          onQuantityChange={handleQuantityChange}
          onRemoveProduct={handleRemoveProduct}
          onDiscountChange={handleDiscountChange}
          onPriceChange={handlePriceChange}
          onToggleCombo={toggleCombo}
          calculateTotals={calculateTotalsWithProductDetails}
          shippingCost={costoEnvioInputValue}
          allowPriceEdit={true}
          clienteNeto={isClienteNetoActive(formData.extraDetails)}
          shippingIncludedInCalculateTotals
            />
          </div>
        </div>
      </div>
      <div className="mt-6 mb-6 flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 sticky bottom-0 bg-gray-50 py-4 border-t border-gray-200">
        {mode !== 'view' && (
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => window.history.back()}
              leftIcon={XMarkIcon}
              fullWidth
              className="sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant={mode === 'create' ? 'success' : 'primary'}
              disabled={!isStockAvailable || isSubmitting}
              isLoading={isSubmitting}
              leftIcon={CheckIcon}
              fullWidth
              className="sm:w-auto"
            >
              {isSubmitting 
                ? (mode === 'create' ? 'Creando...' : 'Guardando...')
                : (mode === 'create' ? 'Crear Cotización' : 'Guardar Cambios')
              }
            </Button>
          </>
        )}
        {/* Botón convertir en pedido solo en modo edit */}
        {mode === 'edit' && (
          <Button
            type="button"
            variant="warning"
            onClick={handleConvertirEnPedido}
            disabled={formData.status === 'APROBADA'}
            leftIcon={ArrowPathIcon}
            fullWidth
            className="sm:w-auto"
          >
            Convertir en pedido
          </Button>
        )}
      </div>
    </form>
  )
}

export default CotizacionForm
